import { useState, useEffect } from "react";
import { Modal } from "./ui/Modal";
import { Button } from "./ui/Button";
import { Field, Input } from "./ui/Input";

// Date-range prompt used before bulk-printing completed Time-off /
// Training requests. Without it a busy agency could fire off 100+
// printed forms in a single click. Defaults to the previous 30 days
// so the supervisor's normal "Tuesday morning, print yesterday's
// approvals" workflow stays one step instead of two.
//
// onConfirm receives the picked range; the calling page uses it to
// filter its `completed` list and then triggers window.print()
// itself (the dialog stays decoupled from the print mechanics so
// either page can wire its own afterprint reset).
export function PrintRangeDialog({
  open, totalCount, onConfirm, onClose,
}: {
  open: boolean;
  // Headline count shown above the date inputs so the supervisor
  // sees how many candidates the unfiltered range would print.
  totalCount: number;
  onConfirm: (range: { start: string; end: string }) => void;
  onClose: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const defaultStart = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  })();
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(today);
  const [err, setErr] = useState<string | null>(null);

  // Re-arm the defaults every time the dialog opens — agencies that
  // print weekly want the same 30-day-back window without resetting
  // it by hand each time.
  useEffect(() => {
    if (!open) return;
    setStart(defaultStart);
    setEnd(today);
    setErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function submit() {
    if (!start || !end) { setErr("Pick a start and end date."); return; }
    if (end < start) { setErr("End date must be on or after start date."); return; }
    onConfirm({ start, end });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Print which range?"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={submit}>Print</Button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-slate-700">
        <p>
          Limits the print run to completed requests whose START
          date falls between these dates. Defaults to the last 30
          days so a routine weekly print doesn't dump every
          historical form. {totalCount} request{totalCount === 1 ? "" : "s"} are
          available in total.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Field label="From">
            <Input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </Field>
          <Field label="To">
            <Input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </Field>
        </div>
        {err && <div className="text-sm text-red-600">{err}</div>}
      </div>
    </Modal>
  );
}
